import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, limit, onSnapshot,
  doc, updateDoc, arrayUnion, arrayRemove, getDoc,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { Trophy, Flame, Star, RefreshCw } from 'lucide-react';

const ARCHETYPES = {
  LOGICAL_ARCHITECT:    { key: 'L', name: '論理的な設計者', emoji: '🧠' },
  EMPATHETIC_CONNECTOR: { key: 'E', name: '共感的な連結者', emoji: '🤝' },
  BOLD_PIONEER:         { key: 'B', name: '大胆な開拓者',   emoji: '🚀' },
  FLEXIBLE_ARTISAN:     { key: 'F', name: '柔軟な実践者',   emoji: '🔥' },
};

const ARCHETYPE_COLORS = {
  LOGICAL_ARCHITECT:    { bg: '#eeedfe', text: '#3c3489', border: '#7f77dd' },
  EMPATHETIC_CONNECTOR: { bg: '#e1f5ee', text: '#085041', border: '#1d9e75' },
  BOLD_PIONEER:         { bg: '#faece7', text: '#4a1b0c', border: '#d85a30' },
  FLEXIBLE_ARTISAN:     { bg: '#faeeda', text: '#412402', border: '#ba7517' },
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'たった今';
  if (diff < 3600) return `${Math.floor(diff/60)}分前`;
  if (diff < 86400) return `${Math.floor(diff/3600)}時間前`;
  return `${Math.floor(diff/86400)}日前`;
};

const FeedCard = ({ item, onReact, currentUid }) => {
  const arc = ARCHETYPES[item.archetypeKey] || ARCHETYPES.LOGICAL_ARCHITECT;
  const col = ARCHETYPE_COLORS[item.archetypeKey] || ARCHETYPE_COLORS.LOGICAL_ARCHITECT;
  const reacted = (item.reactions || []).includes(currentUid);
  const reactionCount = (item.reactions || []).length;
  const is7 = item.dayNum === 7;
  const displayName = item.nickname || `${arc.key}タイプのユーザー`;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: `${is7 ? '1.5px' : '0.5px'} solid ${is7 ? col.border : '#e5e7eb'}`,
      padding: '14px 16px',
      marginBottom: 10,
      boxShadow: is7 ? `0 2px 12px ${col.border}20` : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* アバター */}
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: col.bg, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 18, flexShrink: 0,
        }}>
          {arc.emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* バッジ行 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 5 }}>
            <span style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
              background: col.bg, color: col.text,
            }}>
              {arc.emoji} {arc.key}タイプ
            </span>
            {is7 && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                background: '#fef9c3', color: '#92400e',
              }}>
                🏆 7日間完全突破
              </span>
            )}
            {item.streak >= 3 && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                background: '#fef2f2', color: '#991b1b',
              }}>
                🔥 {item.streak}日連続
              </span>
            )}
          </div>
          {/* 本文 */}
          <p style={{ fontSize: 13, color: '#111827', margin: '0 0 8px', lineHeight: 1.6 }}>
            <strong style={{ color: col.border }}>{displayName}</strong>
            さん（{arc.emoji} {arc.key}タイプ）が
            {is7
              ? <><strong>7日間の突破プランを完全制覇！</strong> 🎉</>
              : <><strong>Day {item.dayNum}</strong> を突破しました！</>
            }
          </p>
          {/* フッター */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatTime(item.createdAt)}</span>
            <button
              onClick={() => onReact(item.id, reacted)}
              style={{
                background: reacted ? '#fef9c3' : '#f9fafb',
                border: `1px solid ${reacted ? '#f59e0b' : '#e5e7eb'}`,
                borderRadius: 99,
                padding: '4px 12px',
                fontSize: 12, fontWeight: reacted ? 700 : 400,
                color: reacted ? '#92400e' : '#6b7280',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
                transition: 'all 0.15s',
              }}
            >
              ⭐ すごい！{reactionCount > 0 && ` ${reactionCount}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AchievementFeedTab = () => {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');
  const [filterType, setFilterType] = useState('all');

  // リアルタイムフィード取得
  useEffect(() => {
    const q = query(
      collection(db, 'breakthroughs'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      setFeed(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ランキング計算
  useEffect(() => {
    if (!feed.length) return;
    const map = {};
    feed.forEach(item => {
      const uid = item.uid;
      if (!map[uid]) {
        map[uid] = {
          uid, nickname: item.nickname, archetypeKey: item.archetypeKey,
          totalDays: 0, hasCompleted7: false, streak: item.streak || 0,
        };
      }
      map[uid].totalDays = Math.max(map[uid].totalDays, item.dayNum);
      if (item.dayNum === 7) map[uid].hasCompleted7 = true;
      map[uid].streak = Math.max(map[uid].streak, item.streak || 0);
    });
    const sorted = Object.values(map)
      .sort((a, b) => b.totalDays - a.totalDays || b.streak - a.streak)
      .slice(0, 10);
    setRanking(sorted);
  }, [feed]);

  const handleReact = useCallback(async (id, reacted) => {
    if (!user) return;
    const ref = doc(db, 'breakthroughs', id);
    await updateDoc(ref, {
      reactions: reacted ? arrayRemove(user.uid) : arrayUnion(user.uid),
    });
  }, [user]);

  const filtered = filterType === 'all'
    ? feed
    : feed.filter(f => {
        const arc = ARCHETYPES[f.archetypeKey];
        return arc && arc.key === filterType;
      });

  const totalReactions = feed.reduce((s, f) => s + (f.reactions || []).length, 0);
  const completed7Count = feed.filter(f => f.dayNum === 7).length;
  const MEDALS = ['🥇','🥈','🥉','4️⃣','5️⃣','6️⃣','7️⃣','8️⃣','9️⃣','🔟'];

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 0 40px' }}>

      {/* サマリーカード */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20,
      }}>
        {[
          { label: '突破記録', value: feed.length, icon: '⚡' },
          { label: '7日間完全制覇', value: completed7Count, icon: '🏆' },
          { label: '総称賛数', value: totalReactions, icon: '⭐' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb',
            padding: '14px 10px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 20 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.2 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* タブ切り替え */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e5e7eb', marginBottom: 16 }}>
        {[
          { id: 'feed', label: '🕐 達成フィード' },
          { id: 'rank', label: '🏆 ランキング' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              background: 'none', border: 'none',
              borderBottom: `2px solid ${activeTab===t.id?'#6366f1':'transparent'}`,
              padding: '8px 16px', fontSize: 13,
              fontWeight: activeTab===t.id ? 700 : 400,
              color: activeTab===t.id ? '#6366f1' : '#6b7280',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'feed' ? (
        <>
          {/* フィルター */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { id: 'all', label: 'すべて' },
              { id: 'L',   label: '🧠 Lタイプ' },
              { id: 'E',   label: '🤝 Eタイプ' },
              { id: 'B',   label: '🚀 Bタイプ' },
              { id: 'F',   label: '🔥 Fタイプ' },
            ].map(f => (
              <button key={f.id} onClick={() => setFilterType(f.id)}
                style={{
                  background: filterType===f.id ? '#f0f0ff' : '#f9fafb',
                  border: `1px solid ${filterType===f.id?'#6366f1':'#e5e7eb'}`,
                  borderRadius: 99, padding: '4px 12px', fontSize: 12,
                  color: filterType===f.id ? '#6366f1' : '#6b7280',
                  cursor: 'pointer', fontWeight: filterType===f.id ? 700 : 400,
                  fontFamily: 'inherit',
                }}>
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 13 }}>
              <div style={{ width: 32, height: 32, border: '2px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
              フィードを読み込み中...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚡</div>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#374151', margin: '0 0 6px' }}>まだ突破記録がありません</p>
              <p style={{ fontSize: 13, margin: '0 0 20px', color: '#9ca3af', lineHeight: 1.6 }}>
                7日間の突破プランを始めると<br/>あなたの記録がここに表示されます
              </p>
              <a href="/breakthrough"
                style={{ display: 'inline-block', padding: '12px 28px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', borderRadius: 12, fontSize: 14, fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}>
                🚀 突破プランを始める
              </a>
              <p style={{ fontSize: 11, color: '#d1d5db', marginTop: 12 }}>
                成長の壁診断 → 7日間プラン → 達成フィードに記録されます
              </p>
            </div>
          ) : (
            filtered.map(item => (
              <FeedCard key={item.id} item={item} onReact={handleReact} currentUid={user?.uid} />
            ))
          )}
        </>
      ) : (
        /* ランキング */
        <div>
          {ranking.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: 13 }}>
              まだランキングデータがありません
            </div>
          ) : (
            ranking.map((r, i) => {
              const arc = ARCHETYPES[r.archetypeKey] || ARCHETYPES.LOGICAL_ARCHITECT;
              const col = ARCHETYPE_COLORS[r.archetypeKey] || ARCHETYPE_COLORS.LOGICAL_ARCHITECT;
              const displayName = r.nickname || `${arc.key}タイプのユーザー`;
              return (
                <div key={r.uid} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px',
                  background: i === 0 ? '#fef9c3' : '#fff',
                  borderRadius: 12,
                  border: `0.5px solid ${i === 0 ? '#f59e0b' : '#e5e7eb'}`,
                  marginBottom: 8,
                }}>
                  <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{MEDALS[i]}</span>
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: col.bg, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 16, flexShrink: 0,
                  }}>
                    {arc.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                      {displayName}
                      <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 6 }}>
                        （{arc.key}タイプ）
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      完了 {r.totalDays}/7日
                      {r.streak >= 2 && ` • 🔥 ${r.streak}日連続`}
                    </div>
                  </div>
                  {r.hasCompleted7 && <span style={{ fontSize: 18 }}>🏆</span>}
                </div>
              );
            })
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AchievementFeedTab;
